import ChainLogo from '@components/ChainLogo'
import TokenAvatar from '@components/TokenAvatar'
import useConfig from '@plug-core/hooks/useConfig'
import { NFT } from '@plug-core/interfaces/common'
import { TokenBalance } from '@plug-core/providers/BalanceProvider'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { shortAddress } from '@plug-core/utils/shortAddress'
import { Avatar, Banner, Heading, ListGroup, ListRow, Text } from '@ui'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  toAddress: string
  token?: TokenBalance
  amount?: string
  nft?: NFT
  warnings: Set<string>
  knownContact?: string
}

const SendDetails: FC<Props> = ({ nft, amount, token, toAddress, warnings, knownContact }) => {
  const { nfts } = useConfig()
  const { t } = useTranslation()

  const renderSelectedNft = () => {
    if (!nft) return

    const collection = nfts.find((c) => c.address === nft.collection)

    return (
      <ListGroup>
        <ListRow
          leading={<Avatar size={40} square src={collection?.logo_url || nft.url} />}
          primary={collection?.name}
          secondary={`#${nft.standard === 'ordinal' ? nft.number : nft.index}`}
        />
      </ListGroup>
    )
  }

  const renderAsset = () => {
    if (nft) return renderSelectedNft()

    const normalizedAmount = Number(amount) * 10 ** -token!.token.decimals

    return (
      <div className="flex flex-col items-center gap-3 py-2">
        <div className="relative">
          <TokenAvatar logo={token?.token.logo} size={48} />
          <span className="absolute -right-1 -bottom-1 grid place-items-center rounded-full bg-sheet p-0.5">
            <ChainLogo chain={token?.token.chain} size={16} />
          </span>
        </div>
        <Heading level={2} tabular align="center" className="max-w-full break-all">
          {formatNumber(normalizedAmount, token!.token.symbol)}
        </Heading>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {renderAsset()}

      <ListGroup>
        <ListRow
          primary={t('send.to')}
          trailing={
            knownContact ? (
              <Text as="span" variant="body-s" weight="semibold" tone="info">
                {knownContact}
              </Text>
            ) : (
              <Text as="span" variant="mono" tone="fg">
                {shortAddress(toAddress, { leftSize: 16 })}
              </Text>
            )
          }
        />
      </ListGroup>

      {[...warnings].map((warning) => (
        <Banner tone="warning" key={warning}>
          {t(`send.warnings.${warning}`)}
        </Banner>
      ))}
    </div>
  )
}

export default SendDetails
